/**
 * Мост ответа spatial_mcp → карта: map_camera + overlay FeatureCollection.
 */

import { applyMcpMapCamera, ZOOM_TO_FEATURES_EVENT } from "@/lib/mcp-map-camera";
import type { SpatialMcpAgentResponse } from "@/types/spatial-mcp-agent";
import { isMcpMapCameraResult } from "@/types/mcp-tools";

export const MCP_SPATIAL_OVERLAY_EVENT = "mcpSpatialResultOverlay";

export type McpSpatialOverlayDetail = {
  featureCollection: GeoJSON.FeatureCollection;
  source?: string;
};

function asGeometry(value: unknown): GeoJSON.Geometry | null {
  if (!value || typeof value !== "object") return null;
  const g = value as GeoJSON.Geometry;
  if (typeof g.type !== "string") return null;
  return g;
}

function featureFromSpatialItem(item: Record<string, unknown>, index: number): GeoJSON.Feature | null {
  const geom = asGeometry(item.geometry);
  if (!geom) return null;
  return {
    type: "Feature",
    properties: {
      object_id: item.object_id ?? null,
      object_name: item.object_name ?? null,
      geometry_id: item.geometry_id ?? null,
      distance_m: item.distance_m ?? null,
      mcp_index: index,
    },
    geometry: geom,
  };
}

/** Достаёт GeoJSON features из результатов MCP tools. */
export function collectFeaturesFromSpatialMcpResponse(
  response: SpatialMcpAgentResponse,
): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = [];
  for (const inv of response.tool_invocations ?? []) {
    if (inv.status !== "success" || inv.result == null) continue;
    const result = inv.result as Record<string, unknown>;

    if (inv.tool_name === "buffer_zone") {
      const geom = asGeometry(result.geometry) ?? asGeometry(result);
      if (geom) {
        features.push({
          type: "Feature",
          properties: { tool: "buffer_zone", ...(result.properties as object) },
          geometry: geom,
        });
      }
      continue;
    }

    if (
      inv.tool_name === "nearest_features" ||
      inv.tool_name === "intersect_features" ||
      inv.tool_name === "search_features"
    ) {
      const list = Array.isArray(result.features) ? result.features : [];
      list.forEach((raw, idx) => {
        if (raw && typeof raw === "object") {
          const f = featureFromSpatialItem(raw as Record<string, unknown>, idx);
          if (f) {
            f.properties = { ...f.properties, tool: inv.tool_name };
            features.push(f);
          }
        }
      });
    }
  }
  return features;
}

function boundsFromFeatures(features: GeoJSON.Feature[]): [number, number, number, number] | null {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  const visit = (coords: unknown): void => {
    if (!Array.isArray(coords) || coords.length === 0) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      const lon = coords[0];
      const lat = coords[1];
      minLon = Math.min(minLon, lon);
      minLat = Math.min(minLat, lat);
      maxLon = Math.max(maxLon, lon);
      maxLat = Math.max(maxLat, lat);
      return;
    }
    for (const c of coords) visit(c);
  };
  for (const f of features) {
    if (f.geometry) visit((f.geometry as GeoJSON.Geometry & { coordinates?: unknown }).coordinates);
  }
  if (!Number.isFinite(minLon) || !Number.isFinite(minLat)) return null;
  return [minLon, minLat, maxLon, maxLat];
}

/**
 * Применяет ответ spatial_mcp к карте (камера + overlay).
 * Возвращает краткую сводку для UI.
 */
export function applySpatialMcpMapBridge(response: SpatialMcpAgentResponse): {
  cameraApplied: boolean;
  featuresShown: number;
} {
  const features = collectFeaturesFromSpatialMcpResponse(response);
  if (features.length > 0) {
    const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features };
    window.dispatchEvent(
      new CustomEvent(MCP_SPATIAL_OVERLAY_EVENT, {
        detail: { featureCollection: fc, source: "spatial_mcp" } satisfies McpSpatialOverlayDetail,
      }),
    );
  }

  let cameraApplied = false;
  if (response.map_camera && isMcpMapCameraResult(response.map_camera)) {
    cameraApplied = applyMcpMapCamera(response.map_camera, { source: "spatial_mcp" });
  } else if (features.length > 0) {
    const bounds = boundsFromFeatures(features);
    if (bounds) {
      window.dispatchEvent(
        new CustomEvent(ZOOM_TO_FEATURES_EVENT, {
          detail: { bounds, padding: 48, maxZoom: 17, source: "spatial_mcp" },
        }),
      );
      cameraApplied = true;
    }
  }

  return { cameraApplied, featuresShown: features.length };
}
